import {
  Center,
  Container,
  Flex,
  SkeletonText,
  Spinner,
  Table,
  useDisclosure,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { type ChangeEvent, type KeyboardEvent, lazy, Suspense, useMemo, useState } from "react"
import { z } from "zod"

import { type MediaOrderBy } from "../../client"
import ActionsMenu from "../../components/Common/ActionsMenu.tsx"
import { PaginationFooter } from "../../components/Common/PaginationFooter.tsx"
import SearchField from "../../components/Common/SearchField.tsx"
import Sort from "../../components/Common/Sort.tsx"
import TitleBar from "../../components/UI/TitleBar"
import { userHasPermission } from "../../utils.ts"
import { NoPermission } from "../../components/UI/no-permission"
import { MediaTableProps, readMedia } from "../../api/media.ts"

const DetailMedia = lazy(() => import("../../components/Media/DetailMedia.tsx"))
const PER_PAGE = 25

const mediaSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/media")({
  component: Media,
  validateSearch: (search) => mediaSearchSchema.parse(search),
})


function MediaTable({ searchValue }: MediaTableProps) {
  // Permission check
  const [me_read] = useMemo(() => {
    return ["read"].map(
      (action) => userHasPermission([{ perm: `media_${action}`, is_allowed: false }])[0].is_allowed
    )
  }, [userHasPermission])

  if(!me_read) return <NoPermission/>

  const fields = [
    { title: "Dateiname", col: "file_name", align: undefined },
    { title: "Dateityp", col: "mime_type", align: undefined },
    { title: "Erstellungsdatum", col: "created_at", align: undefined },
    { title: "ID", col: null, align: undefined },
    { title: "Aktionen", col: null, align: "right" as const },
  ]

  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setPage = (page: number) =>
    navigate({
      search: (prev: { [key: string]: string }) => ({ ...prev, page }),
    })

  // Sorting
  const [columnName, setColumnName] = useState<MediaOrderBy>()
  const [orderBy, setOrderBy] = useState<string>("")
  const [isDesc, setIsDesc] = useState<boolean>()

  // Query
  const queryOptions = useMemo(() =>
    readMedia({
      per_page: PER_PAGE,
      page: page,
      order_by: columnName,
      descending: isDesc,
      query: searchValue,
    }),
  [page, columnName, isDesc, searchValue])

  const {
    data: media,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...queryOptions,
    placeholderData: (prevData) => prevData,
  })

  // Open media detail view
  const detailModal = useDisclosure()
  const [mediaId, setMediaId] = useState("")

  const openModal = (id: string) => {
    setMediaId(id)
    detailModal.onOpen()
  }

  // Sorting
  const sortTable = (name: MediaOrderBy) => {
    if (columnName !== name) {
      setColumnName(name)
      setOrderBy("")
      sorting("")
    } else {
      sorting(orderBy)
    }
  }

  const sorting = (order: string | undefined) => {
    switch (order) {
      case "":
        setOrderBy("desc")
        setIsDesc(true)
        break
      case "desc":
        setOrderBy("asc")
        setIsDesc(false)
        break
      case "asc":
        setOrderBy("")
        setIsDesc(undefined)
        setColumnName(undefined)
        break
    }
  }

  return (
    <>
      {detailModal.open &&
        <Suspense fallback={
          <Center
            bg="rgba(0, 0, 0, 0.3)"
            position="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            zIndex="10000"
          >
            <Spinner size="lg" color="white" />
          </Center>}
        >
          <DetailMedia
            isOpen={detailModal.open}
            onClose={detailModal.onClose}
            mediaId={mediaId}
          />
        </Suspense>
      }

      <Table.ScrollArea asChild>
        <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
          <Table.Header>
            <Table.Row>
              {fields.map((field, i) =>
                field.col != null ? (
                  <Table.ColumnHeader
                    onClick={() => sortTable(field.col as MediaOrderBy)}
                    cursor="pointer"
                    textAlign={field.align}
                    key={i}
                  >
                    {field.title}
                    {columnName === field.col &&
                      <Sort
                        columnNamePassed={columnName}
                        orderByPassed={orderBy}
                      />
                    }
                  </Table.ColumnHeader>
                ) : (
                  <Table.ColumnHeader textAlign={field.align} key={field.title}>
                    {field.title}
                  </Table.ColumnHeader>
                ),
              )}
            </Table.Row>
          </Table.Header>
          {isPending ? (
            <Table.Body>
              <Table.Row>
                {new Array(5).fill(null).map((_, index) => (
                  <Table.Cell key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          ) : (
            <Table.Body>
              {!media?.data ? (
                <Table.Row>
                  <Table.Cell colSpan={5} textAlign="center">
                    Keine Medien verfügbar
                  </Table.Cell>
                </Table.Row>
              ) : (
                media?.data.map((item) => (
                  <Table.Row key={item.id} opacity={isPlaceholderData ? 0.5 : 1}>
                    <Table.Cell onClick={() => openModal(item.id)} cursor="pointer">
                      {item.file_name}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(item.id)} cursor="pointer">
                      {item.mime_type}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(item.id)} cursor="pointer">
                      {new Date(`${item.created_at}`).toLocaleString("de-CH", {
                        timeZone: "Europe/Zurich",
                      })}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(item.id)} cursor="pointer">
                      {item.id}
                    </Table.Cell>

                    <Table.Cell textAlign="right">
                      <ActionsMenu
                        type="Datei"
                        value={item}
                        hideDelete={true}
                        hideEdit={true}
                        file_name={item.file_name}
                        file_path={item.path}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          )}
        </Table.Root>
      </Table.ScrollArea>
      <PaginationFooter
        onChangePage={setPage}
        page={page}
        count={media?.count ? media?.count : 0}
        perPage={PER_PAGE}
      />
    </>
  )
}

function Media() {
  const [inputValue, setInputValue] = useState<string>("")
  const [searchValue, setSearchValue] = useState<string>("")

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (inputValue != null) {
        setSearchValue(inputValue)
      }
    }
  }

  const handleOnClick = () => {
    if (inputValue != null) {
      setSearchValue(inputValue)
    }
  }

  const handleClear = () => {
    setInputValue("")
    if (inputValue != null) {
        setSearchValue("")
    }
  }

  return (
    <Container maxW="full">
      <TitleBar title="Media" />
      <Flex py={8} justify={{base: "flex-start",md: "flex-end"}}>
        <SearchField
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={handleOnClick}
          onClear={handleClear}
        />
      </Flex>
      <MediaTable searchValue={searchValue} />
    </Container>
  )
}
